import {
  AgeBand,
  VA_INTEROCULAR_HIGH,
  VA_INTEROCULAR_MODERATE,
  vaInterocularLines,
  vaStatusForAge,
} from "../utils/lazyEyeRiskModel";
import {
  Badge,
  Box,
  Flex,
  Grid,
  Group,
  Paper,
  RingProgress,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from "@mantine/core";
import { TestType, TumblingEResultData } from "types/testTypes";
import {
  VaStatus,
  getVaHealthScore,
  getVaSnellenString,
  getVaStatusColor,
  getVaStatusLabel,
} from "@utils/test-specific-utils/VisualAcuityUtils";

import { IconEye } from "@tabler/icons-react";
import React from "react";
import TumblingEChartView from "@components/testContainer/progress/customProgressContainers/VAcuity/TumblingEChartView";
import VAcuityResultLine from "@components/testContainer/common/VAcuityResultLine";
import { useElementSize } from "@mantine/hooks";

interface LazyEyeVaCardProps {
  vaTest1: any;
  vaTest2: any;
  ageBand: AgeBand;
  vaLogMar1: number | null;
  vaLogMar2: number | null;
  interocDiffLogMar: number | null;
}

function getEyeLabel(test: any): string {
  if (test?.eye === "leftEye") return "Left Eye";
  if (test?.eye === "rightEye") return "Right Eye";
  return "Both Eyes";
}

function vaStatusColor(status: VaStatus | null): string {
  return status === null ? "gray" : getVaStatusColor(status);
}

function statusMessage(label: string, status: VaStatus | null): string {
  if (status === VaStatus.Normal) return `${label} is within the expected range.`;
  if (status === VaStatus.Borderline)
    return `${label} is slightly below the age-norm — re-check at a routine exam.`;
  if (status === VaStatus.Attention)
    return `${label} is below the referral threshold — comprehensive eval recommended.`;
  return `${label} was not measurable.`;
}

function classifyInterocular(diff: number): VaStatus {
  if (diff >= VA_INTEROCULAR_HIGH) return VaStatus.Attention;
  if (diff >= VA_INTEROCULAR_MODERATE) return VaStatus.Borderline;
  return VaStatus.Normal;
}

function interocularCaption(status: VaStatus, lines: number): string {
  if (status === VaStatus.Attention) {
    return `${lines} lines apart — at the amblyopia cutoff. Comprehensive exam recommended.`;
  }
  if (status === VaStatus.Borderline) {
    return `1 line apart — below the cutoff but worth monitoring.`;
  }
  return `No amblyopia-suggestive asymmetry.`;
}

export function LazyEyeVaCard({
  vaTest1,
  vaTest2,
  ageBand,
  vaLogMar1,
  vaLogMar2,
  interocDiffLogMar,
}: LazyEyeVaCardProps) {
  if (!vaTest1 && !vaTest2) return null;

  const haveBothEyes = interocDiffLogMar != null && Number.isFinite(interocDiffLogMar);
  const interocLines = haveBothEyes ? vaInterocularLines(interocDiffLogMar) : 0;
  // With the 20/40+ floor, two passing eyes always clamp to identical
  // values — there is no resolvable difference to badge.
  const hasResolvableDiff = haveBothEyes && interocLines > 0;
  const interocStatus: VaStatus | null = haveBothEyes
    ? classifyInterocular(interocDiffLogMar)
    : null;

  return (
    <Paper shadow="sm" radius="md" p="md" withBorder>
      <Group mb="sm" gap="md" justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap">
          <Box
            style={{
              width: 40,
              height: 40,
              backgroundColor: "var(--mantine-color-blue-6)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconEye size={20} color="white" />
          </Box>
          <div>
            <Title order={4}>Visual Acuity Test</Title>
            <Text size="sm" c="dimmed">
              How well each eye can see
            </Text>
          </div>
        </Group>

        {haveBothEyes && (
          <Badge
            color={hasResolvableDiff ? vaStatusColor(interocStatus) : "green"}
            size="lg"
            radius="md"
            variant="filled"
            style={{ flexShrink: 0 }}
          >
            {hasResolvableDiff
              ? `Δ ${interocLines} line${interocLines === 1 ? "" : "s"}`
              : "Symmetric"}
          </Badge>
        )}
      </Group>

      <Grid gutter="md">
        {vaTest1 && (
          <Grid.Col span={vaTest2 ? 6 : 12}>
            <VaSingleEyeCard
              vaTest={vaTest1}
              logMar={vaLogMar1}
              label={getEyeLabel(vaTest1)}
              ageBand={ageBand}
            />
          </Grid.Col>
        )}
        {vaTest2 && (
          <Grid.Col span={vaTest1 ? 6 : 12}>
            <VaSingleEyeCard
              vaTest={vaTest2}
              logMar={vaLogMar2}
              label={getEyeLabel(vaTest2)}
              ageBand={ageBand}
            />
          </Grid.Col>
        )}
      </Grid>

      {hasResolvableDiff && (
        <Paper mt="md" p="sm" withBorder radius="md">
          <Text size="sm" c="dimmed">
            <Text span fw={600}>
              Interocular difference:{" "}
            </Text>
            {interocularCaption(interocStatus!, interocLines)}
          </Text>
        </Paper>
      )}
    </Paper>
  );
}

function VaSingleEyeCard({
  vaTest,
  logMar,
  label,
  ageBand,
}: {
  vaTest: any;
  logMar: number | null;
  label: string;
  ageBand: AgeBand;
}) {
  const snellen = getVaSnellenString(vaTest) ?? "N/A";
  const health = getVaHealthScore(vaTest);
  const status = vaStatusForAge(logMar, ageBand);
  const color = vaStatusColor(status);
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";

  const tintBg = isDark
    ? `color-mix(in srgb, transparent, var(--mantine-color-${color}-6) 15%)`
    : `var(--mantine-color-${color}-0)`;
  const tintBorder = `var(--mantine-color-${color}-${isDark ? 6 : 3})`;

  return (
    <Flex gap="md" direction="row" wrap="nowrap">
      <Box style={{ flex: 1 }}>
        <Paper
          p="md"
          withBorder
          radius="md"
          style={{ backgroundColor: tintBg, borderColor: tintBorder }}
        >
          <Group align="flex-start" gap="sm">
            <RingProgress
              size={56}
              thickness={7}
              sections={[{ value: health, color }]}
              label={
                <Text size="10px" ta="center" fw={700} c={`${color}.${isDark ? 3 : 7}`}>
                  {health}%
                </Text>
              }
            />
            <Stack gap={3} style={{ flex: 1 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {label}
              </Text>
              <Text size="22px" fw={700} c={`${color}.${isDark ? 3 : 7}`} lh={1}>
                {snellen}
              </Text>
              <Badge color={color} variant={isDark ? "light" : "filled"} size="xs" w="fit-content">
                {status ? getVaStatusLabel(status) : "N/A"}
              </Badge>
            </Stack>
          </Group>
        </Paper>

        <Paper mt="sm" p="sm" withBorder radius="md">
          <Text size="sm" c="dimmed">
            {statusMessage(label, status)}
          </Text>
        </Paper>
      </Box>

      <Box style={{ width: 264, flexShrink: 0 }}>
        <VaChartPreview vaTest={vaTest} />
      </Box>
    </Flex>
  );
}

function VaChartPreview({ vaTest }: { vaTest: any }) {
  const isTumblingE =
    vaTest.testType === TestType.TumblingE || vaTest.testType === TestType.TumblingEThreshold;
  const slotWidth = 264;
  const innerWidth = isTumblingE ? 500 : 720;
  const scale = slotWidth / innerWidth;

  const { ref, height } = useElementSize();
  const slotHeight = height ? height * scale : slotWidth;

  const result: TumblingEResultData = vaTest.results ?? { responses: [] };
  const values: any[] = vaTest.values ?? [];

  return (
    <Box style={{ width: slotWidth, height: slotHeight, overflow: "hidden", borderRadius: 8 }}>
      <Box
        ref={ref}
        style={{ width: innerWidth, transformOrigin: "top left", transform: `scale(${scale})` }}
      >
        {isTumblingE ? (
          <TumblingEChartView
            esOfInterestIds={result.responses ?? []}
            thresholdRow={result.thresholdRow}
            assumeLargerRowsCorrect={result.thresholdRow != null}
            baseChart={result.chartStimuli}
          />
        ) : (
          <Grid columns={16} align="center">
            {values.map((row: any, index: number) => (
              <VAcuityResultLine
                key={index}
                row={index}
                rowResults={row.results}
                isActive={false}
                letters={row.letters}
                value={row.value}
                isResultLine={index === values.length - 1}
                hideInfo={false}
              />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
